import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { getApps, initializeApp } from "firebase/app";
import {
  getMessaging,
  getToken,
  isSupported as isMessagingSupported,
  Messaging,
  onMessage,
} from "firebase/messaging";
import { useDispatch } from "react-redux";

import History from "../Helpers/History";
import { isWebToNativeDevice } from "@/Helpers/MobileHelper";

import { useAuth } from "./UseAuth";

import NotificationsActions from "@/Redux/Actions/NotificationsActions";
import NotificationsService from "@/Services/NotificationsService";

type DesktopPermission = NotificationPermission | "unsupported";

interface MessagingClient {
  messaging: Messaging;
  registration: ServiceWorkerRegistration;
}

interface ContextValue {
  token: string | undefined;
  notifications: any[];
  notification: any;
  permissionStatus: DesktopPermission;
  requestDesktopPermission: () => Promise<DesktopPermission>;
}

const firebaseConfig = {
  apiKey: "AIzaSyBxi6XZkBcDO9hHlw0pCc7ZaQrsCtIkLfY",
  authDomain: "pl4nner-notifications-test.firebaseapp.com",
  projectId: "pl4nner-notifications-test",
  storageBucket: "pl4nner-notifications-test.firebasestorage.app",
  messagingSenderId: "210181620184",
  appId: "1:210181620184:web:f8f6e1655a49bc8a9672ad",
  measurementId: "G-3Q24DL12NZ",
};

const vapidKey = "BFfinEJa1ILQuBplM3mEoG2BILdPLE2pmVpbYvBS3796gf71oF2Ehjse-6B5znzccW7AgD2WU1vIrMeHpc-Ip9E";
const firebaseAppName = "pl4nner-notifications";

const getNotificationUrl = (data: any = {}) => {
  const { type, project, team, task, chat } = data;

  switch (type) {
    case "team_invitation":
    case "project_delegation":
    case "assistant_assignment":
    case "assistant_unassignment":
    case "account_deleted_by_user":
      return project ? `/project/${project}` : "/";
    case "project_abandonment":
      return project ? `/project/${project}/teams` : "/";
    case "team_member_addition":
    case "team_member_moved":
      return project && team ? `/project/${project}/teams/${team}` : "/";
    case "task_creation":
    case "task_assignment":
    case "task_unassignment":
    case "task_confirmed":
    case "task_rejected":
      return project && task
        ? `/project/${project}/programmation-macro?task_id=${task}`
        : "/";
    case "task_deletion":
      return project ? `/project/${project}/programmation-macro` : "/";
    case "chat_addition":
    case "chat_message":
      return chat ? `/chat/${chat}` : "/chat";
    default:
      return "/";
  }
};

const NotificationsContext = createContext<ContextValue | undefined>(undefined);

export const NotificationsProvider = ({ children }: { children: ReactNode }) => {
  const dispatch = useDispatch();
  const { isAuthenticated } = useAuth();

  const pathname = window.location.pathname;
  const isNotificationRoute = pathname.split("/")[1] === "notification";
  const notificationId = pathname.split("/")[2];

  const [isMobile, setIsMobile] = useState<boolean | undefined>(undefined);
  const [token, setToken] = useState<string | undefined>();
  const [notifications] = useState<any[]>([]);
  const [notification, setNotification] = useState<any>();
  const [permissionStatus, setPermissionStatus] = useState<DesktopPermission>(() => {
    return typeof window !== "undefined" && "Notification" in window
      ? window.Notification.permission
      : "unsupported";
  });

  const messagingClientRef = useRef<Promise<MessagingClient | null> | null>(null);

  const getMessagingClient = useCallback(async (): Promise<MessagingClient | null> => {
    if (
      isMobile !== false ||
      typeof window === "undefined" ||
      !("Notification" in window) ||
      !("serviceWorker" in navigator)
    ) {
      return null;
    }

    if (!messagingClientRef.current) {
      messagingClientRef.current = (async () => {
        if (!(await isMessagingSupported())) {
          setPermissionStatus("unsupported");
          return null;
        }

        const serviceWorkerUrl = `${process.env.PUBLIC_URL || ""}/firebase-messaging-sw.js`;
        const registration = await navigator.serviceWorker.register(serviceWorkerUrl);
        const existingApp = getApps().find((app) => app.name === firebaseAppName);
        const app = existingApp || initializeApp(firebaseConfig, firebaseAppName);

        return {
          messaging: getMessaging(app),
          registration,
        };
      })().catch((error) => {
        console.error("Desktop notifications could not be initialized.", error);
        setPermissionStatus("unsupported");
        messagingClientRef.current = null;
        return null;
      });
    }

    return messagingClientRef.current;
  }, [isMobile]);

  const loadToken = useCallback(async (client?: MessagingClient | null) => {
    const messagingClient = client || await getMessagingClient();

    if (!messagingClient || window.Notification.permission !== "granted") {
      return undefined;
    }

    try {
      const nextToken = await getToken(messagingClient.messaging, {
        vapidKey,
        serviceWorkerRegistration: messagingClient.registration,
      });

      if (nextToken) {
        setToken(nextToken);
      }

      return nextToken || undefined;
    } catch (error) {
      console.error("The desktop notification token could not be obtained.", error);
      return undefined;
    }
  }, [getMessagingClient]);

  const requestDesktopPermission = useCallback(async (): Promise<DesktopPermission> => {
    if (
      isMobile !== false ||
      typeof window === "undefined" ||
      !("Notification" in window)
    ) {
      setPermissionStatus("unsupported");
      return "unsupported";
    }

    try {
      const permission = await window.Notification.requestPermission();
      setPermissionStatus(permission);

      if (permission === "granted") {
        await loadToken();
      }

      return permission;
    } catch (error) {
      console.error("Desktop notification permission could not be requested.", error);
      return permissionStatus;
    }
  }, [isMobile, loadToken, permissionStatus]);

  const loadNotification = useCallback(async () => {
    if (!notificationId) {
      return;
    }

    const { data, error } = await NotificationsService.getNotification(notificationId);

    if (error) {
      console.error(error);
      return;
    }

    setNotification(data);
  }, [notificationId]);

  useEffect(() => {
    const checkIsMobile = async () => {
      setIsMobile(await isWebToNativeDevice());
    };

    checkIsMobile();
  }, []);

  useEffect(() => {
    if (isMobile !== false) {
      return;
    }

    let active = true;
    let unsubscribe: (() => void) | undefined;

    const initializeNotifications = async () => {
      const client = await getMessagingClient();

      if (!active || !client) {
        return;
      }

      if (window.Notification.permission === "granted") {
        setPermissionStatus("granted");
        await loadToken(client);
      }

      unsubscribe = onMessage(client.messaging, (payload) => {
        const data = payload.data || {};
        const title = data.title || payload.notification?.title || "PL4NNER";
        const body = data.body || payload.notification?.body || "";

        if (window.Notification.permission === "granted") {
          const desktopNotification = new window.Notification(title, {
            body,
            data,
            icon: "/logo192.png",
          });

          desktopNotification.onclick = () => {
            desktopNotification.close();
            window.focus();
            History.push(getNotificationUrl(data));
          };
        }

        if (isAuthenticated) {
          dispatch(NotificationsActions.loadResult() as any);
        }
      });
    };

    initializeNotifications();

    return () => {
      active = false;
      unsubscribe?.();
    };
  }, [dispatch, getMessagingClient, isAuthenticated, isMobile, loadToken]);

  useEffect(() => {
    if (!isAuthenticated || !token) {
      return;
    }

    NotificationsService.saveFCMToken(token).catch((error) => {
      console.error("The desktop notification token could not be saved.", error);
    });
  }, [isAuthenticated, token]);

  useEffect(() => {
    if (isNotificationRoute && isAuthenticated) {
      loadNotification();
    }
  }, [isAuthenticated, isNotificationRoute, loadNotification]);

  const value: ContextValue = {
    token,
    notifications,
    notification,
    permissionStatus,
    requestDesktopPermission,
  };

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = (): ContextValue => {
  const context = useContext(NotificationsContext);

  if (context === undefined) {
    throw new Error("useNotifications must be used within a NotificationsProvider");
  }

  return context;
};
